import axios from "axios";
import { eventBus } from "@/main";
import { FREE_LIMITS, FREE_FLAGS, LEGACY_GATED, FEATURE } from "@/constants/entitlements";

const moduleBranch = {
  namespaced: true,
  state: {
    branchInfo: null,
    branchPath: "",
    hasSubscribed: false,
    isFileStorageExpired: true,
    entitlements: null,
    entitlementsLoading: false,
  },
  mutations: {
    setBranchInfo(state, data) {
      if (data && data.clinic) {
        state.branchInfo = data;
        // สาขาเปลี่ยนจริง (path เปลี่ยน) เท่านั้นที่ล้าง entitlements เดิม —
        // ถ้าเป็นการ re-dispatch ของสาขาเดิม (เช่น login cache หมดอายุทุก 5
        // นาทีใน user.js) ต้องไม่ล้าง ไม่งั้น gate จะกระพริบไปใช้ธงเดิมชั่วคราว
        const nextPath = `${data.clinic.code}/${data.code}`;
        if (state.branchPath !== nextPath) state.entitlements = null;
        state.branchPath = nextPath;
        state.hasSubscribed = Boolean(data.fileStoreExpiration);

        const currentDate = new Date();
        const givenDate = new Date(data.fileStoreExpiration);
        givenDate < currentDate
          ? (state.isFileStorageExpired = true)
          : (state.isFileStorageExpired = false);
      } else {
        state.branchInfo = null;
        state.branchPath = "";
        state.hasSubscribed = false;
        state.isFileStorageExpired = true;
        state.entitlements = null;
      }
    },
    setEntitlements(state, data) {
      state.entitlements = data || null;
    },
    setEntitlementsLoading(state, loading) {
      state.entitlementsLoading = Boolean(loading);
    },
  },
  actions: {
    setBranchInfo({ commit, dispatch }, data) {
      commit("setBranchInfo", data);
      // สาขาเปลี่ยน = สิทธิ์เปลี่ยน ต้องดึงใหม่เสมอ ไม่ใช้ของสาขาก่อนหน้า
      if (data && data.clinic && data.clinic.code && data.code) {
        return dispatch("fetchEntitlements", {
          clinicUrl: data.clinic.code,
          branchUrl: data.code,
        });
      }
      commit("setEntitlements", null);
      return Promise.resolve(null);
    },
    // สิทธิ์ล้มเหลวต้องไม่ทำให้หน้าจอพัง — ถ้าเรียกไม่ได้ getters จะถอยไปใช้ธงเดิม
    fetchEntitlements({ commit, state }, payload) {
      // จำสาขาที่ขอไว้ตอนเริ่ม — ถ้าตอบกลับมาช้าแล้วผู้ใช้สลับสาขาไปแล้ว
      // (branchPath ไม่ตรงกับตอนขอ) ต้องทิ้งคำตอบนี้ ห้าม commit ทับสาขาปัจจุบัน
      // เงื่อนไขนี้ดูเหมือนไม่มีทางเป็น false แต่จะเป็น false จริงเวลามี race
      // ระหว่างสลับสาขาเร็วๆ อย่าลบทิ้งว่าเป็นโค้ดตายแล้ว
      const requestedPath = `${payload.clinicUrl}/${payload.branchUrl}`;
      commit("setEntitlementsLoading", true);
      return axios
        .get(
          `/api/branch/${payload.clinicUrl}/${payload.branchUrl}/entitlements`,
          // This is a background diagnostic call — it must never be able to
          // navigate the user anywhere. skipGlobalErrorHandler alone does not
          // opt out of the 403 permission-refresh + redirect-to-/clinic flow
          // in src/config/axios.js; skipPermissionRedirect does.
          { skipGlobalErrorHandler: true, skipPermissionRedirect: true }
        )
        .then((res) => {
          if (state.branchPath !== requestedPath) return null;
          commit("setEntitlements", res.data);

          // เฟส 2 เท่านั้น: สิทธิ์จาก API ควรให้คำตอบเดิมกับธงเดิมทุกสาขา
          // ถ้าไม่ตรง แปลว่า backfill ตกหล่นสาขานี้ — ต้องรู้ก่อนที่จะลบธงเดิมทิ้งในเฟส 5
          const legacySaysSubscribed = !state.isFileStorageExpired;
          const apiSaysSubscribed = Boolean(res.data && res.data.source === "SUBSCRIPTION");
          if (legacySaysSubscribed !== apiSaysSubscribed) {
            console.warn(
              "[entitlements] ไม่ตรงกับธงเดิม",
              { clinicUrl: payload.clinicUrl, branchUrl: payload.branchUrl,
                legacySaysSubscribed, apiSaysSubscribed, planCode: res.data && res.data.planCode }
            );
          }

          return res;
        })
        .catch((err) => {
          console.warn("entitlements unavailable, falling back to legacy flag", err);
          if (state.branchPath !== requestedPath) return null;
          commit("setEntitlements", null);
          return null;
        })
        .finally(() => {
          // ถ้า response นี้เป็นของสาขาเก่าที่ไม่ใช่ปัจจุบันแล้ว ห้ามปิด loading
          // เพราะ request ของสาขาปัจจุบัน (สาขาใหม่) อาจยังโหลดอยู่จริง
          if (state.branchPath !== requestedPath) return;
          commit("setEntitlementsLoading", false);
        });
    },
    updateBranchConfig({ commit, dispatch }, payload) {
      return axios
        .put(
          `/api/branch/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.data
        )
        .then((res) => {
          dispatch("moduleUser/setStateBranchInfo", res.data, { root: true });
          return res;
        })
        .catch((err) => {
          //// Automatic error handling via interceptor
          throw err;
        });
    },
    fetchBranchByUrl({ commit, dispatch }, payload) {
      return axios
        .get(`/api/branch/${payload.clinicUrl}/${payload.branchUrl}`)
        .then((res) => {
          dispatch("setBranchInfo", res.data);
          // Also update moduleUser.branchInfo so other components get updated data
          dispatch("moduleUser/setStateBranchInfo", res.data, { root: true });
          return res;
        })
        .catch((err) => {
          console.error(err);
          // Automatic error handling via interceptor
          throw err;
        });
    },
    checkBranchMember({ commit }, payload) {
      return axios
        .get(
          `/api/branchMember/unAuthorizeUser/${payload.clinicUrl}/${payload.branchUrl}/${payload.email}`,
          { skipGlobalErrorHandler: true }
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
       
          throw err;
        });
    },
    fetchBranchMember({ commit }, payload) {
      return axios
        .get(`/api/branchMember/${payload.clinicUrl}/${payload.branchUrl}`)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    fetchBranchMemberIncludeDeleted({ commit }, payload) {
      return axios
        .get(`/api/branchMember/${payload.clinicUrl}/${payload.branchUrl}`, {
          params: { activeMembersOnly: false },
        })
        .then((res) => {
          return res;
        })
        .catch((err) => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    createBranchMember({ commit }, payload) {
      return axios
        .post(
          `/api/branchMember/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.member
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    updateBranchMember({ commit }, payload) {
      return axios
        .put(
          `/api/branchMember/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.member
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    deleteBranchMember({ commit }, payload) {
      return axios
        .delete(
          `/api/branchMember/${payload.clinicUrl}/${payload.branchUrl}/${payload.uid}`
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    createAndUpdateDooctorWorkingHours({ commit }, payload) {
      return axios
        .post(
          `/api/branchMember/doctorWorkingHour/${payload.clinicUrl}/${payload.branchUrl}`,
          {
            branchMemberId: payload.branchMemberId,
            doctorWorkingHours: payload.doctorWorkingHours
          }
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          // Automatic error handling via interceptor
          throw err;
        });
    },
    // Room Management Actions
    fetchRoomList({ commit }, payload) {
      return axios
        .get(`/api/room/${payload.clinicUrl}/${payload.branchUrl}`)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    createRoom({ commit }, payload) {
      return axios
        .post(
          `/api/room/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.room
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    updateRoom({ commit }, payload) {
      return axios
        .put(
          `/api/room/${payload.clinicUrl}/${payload.branchUrl}/${payload.roomId}`,
          payload.room
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    deleteRoom({ commit }, payload) {
      return axios
        .delete(
          `/api/room/${payload.clinicUrl}/${payload.branchUrl}/${payload.roomId}`
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    // Receipt Payment Method Management Actions
    fetchReceiptPaymentList({ commit }, payload) {
      return axios
        .get(`/api/master/receiptPayment/${payload.clinicUrl}/${payload.branchUrl}`)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    createReceiptPayment({ commit }, payload) {
      return axios
        .post(
          `/api/master/receiptPayment/${payload.clinicUrl}/${payload.branchUrl}`,
          payload.receiptPayment
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    updateReceiptPayment({ commit }, payload) {
      return axios
        .put(
          `/api/master/receiptPayment/${payload.clinicUrl}/${payload.branchUrl}/${payload.receiptPaymentId}`,
          payload.receiptPayment
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    deleteReceiptPayment({ commit }, payload) {
      return axios
        .delete(
          `/api/master/receiptPayment/${payload.clinicUrl}/${payload.branchUrl}/${payload.receiptPaymentId}`
        )
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
    fetchPaymentCategories({ commit }, payload) {
      return axios
        .get(`/api/master/mReceiptPaymentCate/${payload.clinicUrl}/${payload.branchUrl}`)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          throw err;
        });
    },
  },
  getters: {
    getBranchInfo: (state) => {
      return state.branchInfo;
    },
    getClinicInfo: (state) => {
      if (state.branchInfo && state.branchInfo.clinic) {
        return state.branchInfo.clinic;
      } else {
        return null;
      }
    },
    getRoomList: (state) => {
      if (state.branchInfo && state.branchInfo.rooms) {
        return state.branchInfo.rooms;
      } else {
        return [];
      }
    },
    getRoomListBy: (state, getters) => (value, key) => {
      return getters.findBy(state.branchInfo.rooms, value, key, {
        value: null,
        label: "-",
      });
    },
    getAuthorization: (state) => {
      if (state.branchInfo && state.branchInfo.auth) {
        return state.branchInfo.auth;
      } else {
        return { authId: 0 };
      }
    },
    getBranchPath: (state) => {
      return state.branchPath;
    },
    isFileStorageExpired: (state) => {
      return state.isFileStorageExpired;
    },
    hasSubscribed: (state) => {
      return state.hasSubscribed;
    },
    getEntitlements: (state) => state.entitlements,
    getPlanCode: (state) => (state.entitlements ? state.entitlements.planCode : null),
    getSubscriptionStatus: (state) => (state.entitlements ? state.entitlements.status : null),
    getDaysRemaining: (state) =>
      state.entitlements ? state.entitlements.daysRemaining : null,
    getSubscriptionEndDate: (state) =>
      state.entitlements ? state.entitlements.endDate : null,
    // แพ็กเดิมของลูกค้าก่อนระบบแพ็กใหม่ (ปิดขาย) — BE รวมสิทธิ์เข้า entitlements ให้แล้ว
    // ตัวนี้ใช้แสดงแถบบอกวันหมดเท่านั้น ห้ามเอาไปรวมสิทธิ์ซ้ำ
    getLegacyGrant: (state) => (state.entitlements && state.entitlements.legacy) || null,
    // true = จะถูกตัดบัตรอัตโนมัติ · false = จะหมดอายุเฉย ๆ ต้องมาต่อเอง
    // สองอย่างนี้เป็นคนละข้อความและคนละปุ่มบนหน้าจอ status อย่างเดียวบอกไม่ได้
    // เพราะ PromptPay กับบัตรที่ยกเลิกแล้วต่างก็เป็น false เหมือนกัน
    isAutoRenewOn: (state) => Boolean(state.entitlements && state.entitlements.autoRenew),
    // โควตา MCP เป็นกองกลางระดับคลินิก ไม่ใช่ของสาขา จึงอยู่นอก map entitlements
    getClinicMcpQuota: (state) =>
      state.entitlements ? state.entitlements.clinicMcpQuota : null,
    getClinicMcpUsed: (state) =>
      state.entitlements ? state.entitlements.clinicMcpUsed : null,

    // ใช้ไปเท่าไรแล้ว — คู่กับ entitlementLimit ที่เป็นเพดาน
    //
    // คืน null แปลว่า **ไม่รู้** ไม่ใช่ "ยังไม่ได้ใช้" ซึ่งเกิดได้สองทาง: ยังไม่มีข้อมูลจาก
    // API เลย (เส้นทางถอยของเฟส 2) หรือ BE บอกเองว่ายังไม่มีแหล่งข้อมูล (DENTCARE_CHATBOT
    // ที่รอ DentCare รายงานกลับมา) ผู้เรียกต้องซ่อนตัวเลขไป ไม่ใช่วาด 0
    //
    // ฟีเจอร์เปิด/ปิดไม่มีคีย์ used มาเลยโดยตั้งใจ — ถอยมาเป็น null เหมือนกัน
    entitlementUsed: (state) => (code) => {
      const map = state.entitlements && state.entitlements.entitlements;
      if (!map || !map[code]) return null;
      const used = map[code].used;
      return used === undefined ? null : used;
    },

    // ฟีเจอร์เปิด/ปิด — คืน true ถ้าใช้ได้
    // ไม่มีข้อมูล = ถอยไปใช้ธงเดิมได้เฉพาะ code ใน LEGACY_GATED เท่านั้น (มี
    // subscription = ใช้ได้ ให้ผลเท่าวันนี้) — code อื่นที่ธงเดิมไม่เคยคุม ถอยไป
    // ใช้ค่า FREE_FLAGS แทน (false วันนี้ทุกตัว) ไม่ใช่ธงเดิม ไม่งั้นจะกลายเป็น
    // แจกฟีเจอร์ที่ไม่เคยขายให้ทุกสาขาที่มี subscription เดิม
    entitlementFlag: (state) => (code) => {
      const map = state.entitlements && state.entitlements.entitlements;
      if (map && map[code]) return Boolean(map[code].value);

      return LEGACY_GATED.includes(code)
        ? !state.isFileStorageExpired
        : Boolean(FREE_FLAGS[code]);
    },

    // โควตาจำนวน — คืน number หรือ null (null = ไม่จำกัด)
    // ไม่มีข้อมูล = ถอยไปใช้ธงเดิมได้เฉพาะ code ใน LEGACY_GATED เท่านั้น: มี
    // subscription -> ไม่จำกัด, ไม่มี -> ค่า FREE_LIMITS (0 ถ้าไม่มีนิยามไว้)
    // code อื่นที่ธงเดิมไม่เคยคุมไม่มี "unlimited" ให้ถอยไปใช้เลย — ใช้ค่า
    // FREE_LIMITS เสมอ (0 ถ้าไม่มีนิยามไว้ = ปิดฟีเจอร์ไว้ก่อน ซึ่งเป็นค่า
    // ปลอดภัยสุดสำหรับ code ที่ไม่เคยมีอยู่ในธงเดิม)
    entitlementLimit: (state) => (code) => {
      const map = state.entitlements && state.entitlements.entitlements;
      if (map && map[code]) return map[code].value;

      if (!LEGACY_GATED.includes(code)) {
        return FREE_LIMITS[code] !== undefined ? FREE_LIMITS[code] : 0;
      }

      return state.isFileStorageExpired
        ? (FREE_LIMITS[code] !== undefined ? FREE_LIMITS[code] : 0)
        : null;
    },

    // จุดเดียวที่ตัดสินว่าสาขานี้ควรเห็นโฆษณาไหม — ทุกหน้าที่มีโฆษณา
    // (QueueManage.vue, DoctorDashboard.vue, ...) ต้อง map getter ตัวนี้แทนที่
    // จะเขียน computed ของตัวเองซ้ำ ไม่งั้นกฎจะเพี้ยนไปทีละหน้าเมื่อมีหน้าที่ 3
    showAds: (state, getters) => !getters.entitlementFlag(FEATURE.AD_FREE),
  },
};
export default moduleBranch;
